import { SwitchAiReviewModeEventProperties } from "@/analytics/ai-detection-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { selectAiDetectedMarkupsNotReviewedCount } from "@/store/ai-asset-review/ai-asset-review-data-selectors";
import { selectAiReviewViewMode } from "@/store/ai-asset-review/ai-asset-review-selectors";
import { Analytics } from "@faro-lotv/foreign-observers";

import {
  AiReviewViewMode,
  setViewMode,
} from "@/store/ai-asset-review/ai-asset-review-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FaroButton, FaroText, FontWeights, grey } from "@faro-lotv/flat-ui";
import { Box, Stack } from "@mui/material";
import { useCallback, type JSX } from "react";

/** @returns main content header component with annotation count and view mode toggles */
export function AiReviewMainContentHeader(): JSX.Element {
  const dispatch = useAppDispatch();
  const viewMode = useAppSelector(selectAiReviewViewMode);
  const assetsToReviewCount = useAppSelector(
    selectAiDetectedMarkupsNotReviewedCount,
  );

  const handleViewModeChange = useCallback(
    (newViewMode: AiReviewViewMode) => {
      Analytics.track<SwitchAiReviewModeEventProperties>(
        EventType.switchAiReviewMode,
        {
          mode:
            newViewMode === AiReviewViewMode.stepByStep
              ? "step-by-step"
              : "list-view",
          remainingDetections: assetsToReviewCount,
        },
      );
      dispatch(setViewMode(newViewMode));
    },
    [dispatch, assetsToReviewCount],
  );

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 2, sm: 0 }}
      sx={{
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
      }}
    >
      {/* Left side: Annotation count */}
      <Stack
        spacing={1}
        sx={{ alignItems: { xs: "center", sm: "flex-start" } }}
      >
        <FaroText
          variant="labelL"
          color={grey[800]}
          fontWeight={FontWeights.SemiBold}
        >
          {assetsToReviewCount} AI generated annotation
          {assetsToReviewCount === 1 ? "" : "s"} to review
        </FaroText>
      </Stack>

      {/* Right side: View mode toggle buttons */}
      <Box
        component="div"
        sx={{
          backgroundColor: grey[50],
          borderRadius: 1,
          p: 0.5,
          display: "flex",
          alignSelf: { xs: "center", sm: "auto" },
          width: { xs: "fit-content", sm: "auto" },
        }}
      >
        <FaroButton
          variant="ghost"
          size="s"
          sx={{
            minWidth: "auto",
            backgroundColor:
              viewMode === AiReviewViewMode.stepByStep
                ? "background.paper"
                : "transparent",
            color:
              viewMode === AiReviewViewMode.stepByStep
                ? "primary.main"
                : grey[600],
          }}
          onClick={() => handleViewModeChange(AiReviewViewMode.stepByStep)}
        >
          Step by step
        </FaroButton>
        <FaroButton
          variant="ghost"
          size="s"
          sx={{
            minWidth: "auto",
            backgroundColor:
              viewMode === AiReviewViewMode.listView
                ? "background.paper"
                : "transparent",
            color:
              viewMode === AiReviewViewMode.listView
                ? "primary.main"
                : grey[600],
          }}
          onClick={() => handleViewModeChange(AiReviewViewMode.listView)}
        >
          List view
        </FaroButton>
      </Box>
    </Stack>
  );
}
